import { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { UserButton } from "@clerk/react";
import { LayoutDashboard, Layers, BellRing, Warehouse, Users, Settings, Menu, Truck, Coins } from "lucide-react";
import { ThemeToggle } from "@/lib/theme";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useApi } from "@/lib/api";
import { eur, pct } from "@/lib/format";
import { useMe } from "@/context/MeContext";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, id: "dashboard" },
  { to: "/kurse", label: "Kurse & Sorten", icon: Layers, id: "categories" },
  { to: "/metalle", label: "Metallkurse", icon: Coins, id: "metals" },
  { to: "/alarme", label: "Preisalarme", icon: BellRing, id: "alerts" },
  { to: "/wertstoffhoefe", label: "Wertstoffhöfe", icon: Warehouse, id: "yards" },
  { to: "/anlieferungen", label: "Anlieferungen", icon: Truck, id: "deliveries" },
  { to: "/benutzer", label: "Benutzer", icon: Users, id: "users", admin: true },
  { to: "/einstellungen", label: "Einstellungen", icon: Settings, id: "settings", admin: true },
];

const NavItems = ({ onClick }) => {
  const me = useMe();
  return (
    <nav className="space-y-1">
      {NAV.filter((n) => !n.admin || me.role === "admin").map((n) => (
        <NavLink
          key={n.to}
          to={n.to}
          end={n.to === "/"}
          onClick={onClick}
          data-testid={`nav-item-${n.id}`}
          className={({ isActive }) =>
            `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors ${
              isActive ? "bg-orange-500/10 text-orange-700 border border-orange-500/20" : "text-slate-500 hover:bg-slate-100 hover:text-slate-900 border border-transparent"
            }`
          }
        >
          <n.icon className="h-4 w-4" />
          {n.label}
        </NavLink>
      ))}
    </nav>
  );
};

const Logo = () => (
  <div className="flex items-center gap-3 px-2">
    <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br from-amber-500 to-orange-700 font-display font-bold text-white">Cu</div>
    <div>
      <div className="font-display text-lg font-semibold leading-none tracking-tight">KupferRadar</div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Börse · Schrott · Alarm</div>
    </div>
  </div>
);

const Ticker = () => {
  const api = useApi();
  const [snap, setSnap] = useState(null);
  useEffect(() => {
    const load = () => api.get("/prices/current").then((r) => setSnap(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [api]);
  if (!snap) return <span className="text-xs text-slate-500">Kurs wird geladen…</span>;
  const up = snap.change_pct >= 0;
  return (
    <div className="flex items-center gap-3 text-sm" data-testid="header-ticker">
      <span className="pulse-dot" />
      <span className="hidden text-slate-500 sm:inline">Kupfer COMEX</span>
      <span className="num font-semibold">{eur(snap.eur_per_kg, 3)}/kg</span>
      <span className={`num text-xs ${up ? "text-emerald-600" : "text-red-600"}`}>{pct(snap.change_pct)}</span>
    </div>
  );
};

export default function Layout({ children }) {
  const me = useMe();
  const [open, setOpen] = useState(false);
  return (
    <div className="relative z-10 flex min-h-screen">
      <aside className="sticky top-0 hidden h-screen w-64 shrink-0 flex-col justify-between border-r border-slate-200 bg-white p-5 lg:flex">
        <div className="space-y-8">
          <Logo />
          <NavItems />
        </div>
        <div className="truncate px-2 text-xs text-slate-500" data-testid="sidebar-user-email">{me.email}</div>
      </aside>
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="copper-bar" />
        <header className="sticky top-0 z-20 flex h-16 items-center justify-between gap-4 border-b border-slate-200 bg-white/80 px-4 backdrop-blur-xl sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <button className="rounded-lg p-2 text-slate-600 hover:bg-slate-100 lg:hidden" data-testid="mobile-menu-btn" aria-label="Menü">
                  <Menu className="h-5 w-5" />
                </button>
              </SheetTrigger>
              <SheetContent side="left" className="w-72 border-slate-200 bg-white p-5">
                <div className="space-y-8 pt-4">
                  <Logo />
                  <NavItems onClick={() => setOpen(false)} />
                </div>
              </SheetContent>
            </Sheet>
            <Ticker />
          </div>
          <div className="flex items-center gap-3" data-testid="user-profile-menu">
            <ThemeToggle />
            <span className="hidden rounded-full border border-slate-200 px-2.5 py-1 text-[11px] uppercase tracking-wider text-slate-500 sm:inline">
              {me.role === "admin" ? "Admin" : "Benutzer"}
            </span>
            <UserButton />
          </div>
        </header>
        <main className="mx-auto w-full max-w-[1400px] flex-1 px-4 py-6 sm:px-6 lg:px-8 lg:py-8">{children}</main>
      </div>
    </div>
  );
}
